<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ProvisionalMark from '@/components/ProvisionalMark.vue'
import UserName from '@/components/UserName.vue'
import { podiumClass } from '@/lib/podium'
import { deltaColor, deltaIcon, formatDelta, formatRating } from '@/lib/rating'

/**
 * Overall ranking: the players by rating.
 *
 * It only renders the page it is given — loading, errors and paging belong to the caller (see
 * StandingsView) — so the same table can be dropped wherever a page of the ranking is available.
 */
const props = defineProps({
  /** Ranking rows, in the shape returned by GET /api/standings. */
  players: { type: Array, default: () => [] },
  /**
   * Row of the logged-in player when it is *not* on this page (null otherwise): it stays pinned
   * under the table, so a player never has to browse to the end to find themselves.
   */
  me: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  /** Page on the screen, 1-based. */
  page: { type: Number, default: 1 },
  lastPage: { type: Number, default: 1 },
  /** Players in the whole ranking, not only on this page. */
  total: { type: Number, default: 0 },
  /** Players per page, to spell out the range under the table. */
  perPage: { type: Number, default: 20 },
  /** Window of the recent columns (`{ days }`), from the same payload. */
  period: { type: Object, default: null },
})

const emit = defineEmits(['update:page'])

const { t } = useI18n()

/** "1-20 of 137": the slice on the screen out of the whole ranking. */
const range = computed(() => ({
  from: props.total ? (props.page - 1) * props.perPage + 1 : 0,
  to: Math.min(props.page * props.perPage, props.total),
}))

/**
 * Shape of the loading placeholder: a header row and as many rows as the page will hold, so the
 * block keeps its height when the ranking arrives (see v-skeleton-loader).
 */
const skeletonType = computed(() => `table-thead, table-row@${props.perPage}`)

/** Days of the recent window: both new columns depend on it. */
const periodDays = computed(() => props.period?.days ?? null)
</script>

<template>
  <v-card elevation="3" rounded="lg" class="overflow-hidden">
    <div class="d-flex align-center ga-3 pa-4">
      <v-avatar color="amber-darken-2" variant="tonal" size="48">
        <v-icon size="28">mdi-trophy-variant</v-icon>
      </v-avatar>
      <div class="flex-grow-1">
        <h2 class="text-h6 font-weight-bold">{{ t('navigation.ranking') }}</h2>
        <!-- <p class="text-caption text-medium-emphasis mb-0">{{ t('standings.rules') }}</p> -->
      </div>
    </div>

    <v-divider />

    <!-- Loading: a placeholder in the shape of the table being loaded (header row plus one row per
         row of the page) instead of a spinner that shows nothing of what is coming. -->
    <v-skeleton-loader
      v-if="loading"
      :type="skeletonType"
      :loading-text="t('standings.loading')"
    />

    <div v-else-if="!players.length" class="text-center pa-8">
      <v-icon size="56" color="medium-emphasis" class="mb-3">mdi-trophy-outline</v-icon>
      <p class="text-medium-emphasis mb-0">{{ t('standings.empty') }}</p>
    </div>

    <template v-else>
      <v-table hover class="standings-table">
        <thead>
          <tr>
            <th class="text-center" style="width: 64px">{{ t('standings.position') }}</th>
            <th>{{ t('standings.player') }}</th>
            <th class="text-center">{{ t('standings.rating') }}</th>
            <template v-if="periodDays">
              <th class="text-center text-no-wrap">
                <v-tooltip :text="t('standings.recentDeltaTooltip', { days: periodDays })">
                  <template v-slot:activator="{ props: tip }">
                    <span v-bind="tip">{{ t('standings.recentDelta', { days: periodDays }) }}</span>
                  </template>
                </v-tooltip>
              </th>
              <th class="text-center d-none d-sm-table-cell">
                <v-tooltip :text="t('standings.recentRacesTooltip', { days: periodDays })">
                  <template v-slot:activator="{ props: tip }">
                    <span v-bind="tip">{{ t('standings.recentRaces') }}</span>
                  </template>
                </v-tooltip>
              </th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="player in players" :key="player.userId" :class="{ 'me-row': player.isMe }">
            <td class="text-center">
              <v-icon v-if="player.position <= 3" :class="podiumClass(player.position)" size="20">mdi-medal</v-icon>
              <span v-else class="text-medium-emphasis">{{ player.position }}</span>
            </td>
            <td class="font-weight-medium"><UserName :name="player.playerName" /></td>
            <td class="text-center font-weight-bold">
              {{ formatRating(player.rating) }}<ProvisionalMark v-if="player.provisional" />
            </td>
            <template v-if="periodDays">
              <td class="text-center text-no-wrap">
                <template v-if="player.periodDelta !== null">
                  <v-icon
                    :icon="deltaIcon(player.periodDelta)"
                    :color="deltaColor(player.periodDelta)"
                    size="16"
                    class="me-1"
                  />
                  <span :class="`text-${deltaColor(player.periodDelta)}`">
                    {{ formatDelta(player.periodDelta) }}
                  </span>
                </template>
                <span v-else class="text-medium-emphasis">–</span>
              </td>
              <td class="text-center d-none d-sm-table-cell">{{ player.periodRaces || '–' }}</td>
            </template>
          </tr>
        </tbody>

        <!-- The player outside the page: own row, marked as such, in a body of its own so the
             table can draw the separation line all the way across. -->
        <tbody v-if="me" class="pinned">
          <tr class="me-row">
            <td class="text-center text-medium-emphasis">{{ me.position }}</td>
            <td class="font-weight-medium">
              <UserName :name="me.playerName" />
              <v-chip class="ms-2" color="primary" size="x-small" variant="tonal" label>
                {{ t('standings.yourPosition') }}
              </v-chip>
            </td>
            <td class="text-center font-weight-bold">
              {{ formatRating(me.rating) }}<ProvisionalMark v-if="me.provisional" />
            </td>
            <template v-if="periodDays">
              <td class="text-center text-no-wrap">
                <template v-if="me.periodDelta !== null">
                  <v-icon
                    :icon="deltaIcon(me.periodDelta)"
                    :color="deltaColor(me.periodDelta)"
                    size="16"
                    class="me-1"
                  />
                  <span :class="`text-${deltaColor(me.periodDelta)}`">
                    {{ formatDelta(me.periodDelta) }}
                  </span>
                </template>
                <span v-else class="text-medium-emphasis">–</span>
              </td>
              <td class="text-center d-none d-sm-table-cell">{{ me.periodRaces || '–' }}</td>
            </template>
          </tr>
        </tbody>
      </v-table>

      <v-divider />

      <div class="d-flex flex-wrap align-center ga-3 pa-4">
        <span class="text-caption text-medium-emphasis">
          {{ t('standings.range', { from: range.from, to: range.to, total }) }}
        </span>
        <v-spacer />
        <v-pagination
          v-if="lastPage > 1"
          :model-value="page"
          :length="lastPage"
          :total-visible="7"
          density="comfortable"
          @update:model-value="emit('update:page', $event)"
        />
      </div>
    </template>
  </v-card>
</template>

<style scoped>
/* The logged-in player stands out without breaking the row rhythm of the table. */
.me-row td {
  background-color: rgba(var(--v-theme-primary), 0.06);
}

/* The pinned row is not the 21st of the page: the line makes it read as a footnote. */
.pinned td {
  border-top: 2px solid rgba(var(--v-theme-primary), 0.35);
}
</style>
